import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FindingRow {
  readonly findingId: string;
  readonly findingCode: string;
  readonly title: string;
  readonly category: string;
  readonly severity: string;
  readonly status: string;
}

const SEVERITIES = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
const STATUSES = ['', 'OPEN', 'CONFIRMED', 'IN_REMEDIATION', 'REMEDIATED', 'RETESTED', 'CLOSED', 'ACCEPTED_RISK', 'FALSE_POSITIVE'];
const CATEGORIES = [
  '', 'INJECTION', 'BROKEN_ACCESS_CONTROL', 'CRYPTO_FAILURE', 'INSECURE_DESIGN', 'SEC_MISCONFIG',
  'VULNERABLE_COMPONENTS', 'AUTH_FAILURE', 'INTEGRITY_FAILURE', 'LOGGING_FAILURE', 'SSRF', 'BUSINESS_LOGIC', 'OTHER',
];

/**
 * GR L30.09 — Penetration Test Findings (`/security/pentest`). Register of the
 * GR net-new `pentest_finding` store: findings from penetration testing rounds
 * with their OWASP-aligned category, severity and remediation status. Filterable
 * by severity, status and category.
 */
export function GrPentestPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<FindingRow[]>([]);
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (severity) params.set('severity', severity);
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    const qs = params.toString();
    apiRequest<{ data: FindingRow[] }>(`/v1/security/pentest/findings${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, severity, status, category]);

  return (
    <main aria-label="security-pentest">
      <h1>{t('gr30.pentest.title')}</h1>
      <div className="filters">
        <label>
          {t('gr30.pentest.labelSeverity')}
          <select aria-label="filter-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr30.pentest.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.pentest.labelStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr30.pentest.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.pentest.labelCategory')}
          <select aria-label="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('gr30.pentest.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr30.pentest.loading')}</p> : (
        <>
          <p aria-label="finding-count">{t('gr30.pentest.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr30.pentest.empty')}</p> : (
            <table aria-label="finding-table">
              <thead><tr><th>{t('gr30.pentest.colCode')}</th><th>{t('gr30.pentest.colTitle')}</th><th>{t('gr30.pentest.colCategory')}</th><th>{t('gr30.pentest.colSeverity')}</th><th>{t('gr30.pentest.colStatus')}</th></tr></thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.findingId}>
                    <td>{f.findingCode}</td><td>{f.title}</td><td>{f.category}</td>
                    <td>{f.severity}</td><td>{f.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
